import { useCallback } from 'react';
import { useOne, useUpdate, useGo } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  FormStickyActions,
  FormSpacer,
  FormHeader,
} from '@/components/forms';
import type { Broadcast } from './types';
import { BroadcastFormLayout } from './components';
import { useBroadcastForm } from './hooks';

const EDITABLE_STATUSES: ReadonlyArray<Broadcast['status']> = ['DRAFT', 'SCHEDULED'];

export function BroadcastsEdit() {
  const go = useGo();
  const { id } = useParams<{ id: string }>();

  const broadcastResult = useOne<Broadcast>({ resource: 'broadcasts', id: Number(id) });
  const broadcast = broadcastResult.result;
  const isLoading = broadcastResult.query?.isLoading ?? false;

  const updateMutation = useUpdate();
  const isPending = updateMutation.mutation?.isPending ?? false;

  const form = useBroadcastForm({ initialData: broadcast });

  const handleGoBack = useCallback(() => go({ to: `/broadcasts/show/${id}` }), [go, id]);
  const handleGoList = useCallback(() => go({ to: '/broadcasts' }), [go]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const payloadData = form.buildSubmitPayload();
    updateMutation.mutate(
      { resource: 'broadcasts', id: Number(id), values: payloadData },
      { onSuccess: () => go({ to: `/broadcasts/show/${id}` }) },
    );
  }, [form, updateMutation, id, go]);

  // ─── Early returns ─────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!broadcast) {
    return (
      <div className="space-y-6">
        <FormHeader title="Рассылка не найдена" onBack={handleGoList} sticky />
        <div className="flex flex-col items-center gap-4 py-12">
          <AlertTriangle className="h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">Рассылка с ID {id} не найдена</p>
          <Button variant="outline" onClick={handleGoList}>Назад к списку</Button>
        </div>
      </div>
    );
  }

  if (!EDITABLE_STATUSES.includes(broadcast.status)) {
    return (
      <div className="space-y-6">
        <FormHeader title={`Редактирование: ${broadcast.name}`} onBack={handleGoBack} sticky />
        <div className="flex flex-col items-center gap-4 py-12">
          <AlertTriangle className="h-12 w-12 text-yellow-500" />
          <p className="text-muted-foreground">
            Редактирование недоступно: рассылка имеет статус <strong>{broadcast.status}</strong>.
            Можно редактировать только рассылки со статусом DRAFT или SCHEDULED.
          </p>
          <Button variant="outline" onClick={handleGoBack}>Назад к просмотру</Button>
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <FormHeader
        title={`Редактирование: ${broadcast.name}`}
        description="Редактирование рассылки с поддержкой мультиязычности"
        onBack={handleGoBack}
        sticky
      />

      <BroadcastFormLayout
        formId="broadcast-edit-form"
        form={form}
        isPending={isPending}
        onSubmit={handleSubmit}
      />

      <FormSpacer />

      <FormStickyActions>
        <Button type="submit" form="broadcast-edit-form" disabled={isPending || !form.hasChannels}>
          {isPending ? 'Сохранение...' : 'Сохранить'}
        </Button>
        <Button type="button" variant="outline" onClick={form.handleOpenPreview} disabled={isPending || !form.previewLanguage}>
          Предпросмотр
        </Button>
        <Button type="button" variant="outline" onClick={handleGoBack} disabled={isPending}>
          Отмена
        </Button>
      </FormStickyActions>
    </div>
  );
}
